import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import {
  ApexChart,
  ApexAxisChartSeries,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexGrid,
  ApexNonAxisChartSeries,
  ApexXAxis
} from "ng-apexcharts";

export type ChartOptions = {
  series: ApexAxisChartSeries;  // Hapus union type dengan undefined
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  grid: ApexGrid;
  colors: string[];
  legend: ApexLegend;
};

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Sales, SalesPropertyState, SalesPropertyStateResponse, SalesResponse, TotalEventProperty, TotalEventPropertyResponse, TransStatus } from '../../core/models/sales';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SalesActivitiesService } from '../../core/services/sales-activities.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginationConfig } from '../../core/interface/PaginationConfig';
import { SortConfig } from '../../core/interface/SortConfig';
import { SalesTeam } from '../../core/models/list-sales-performance';

@Component({
  selector: 'app-sales-activities',
  templateUrl: './sales-activities.component.html',
  styleUrl: './sales-activities.component.css'
})
export class SalesActivitiesComponent implements OnInit {

  @ViewChild("chart") chart!: ChartComponent;
  public chartOptions!: ChartOptions;
  private resizeListener!: () => void;
  private searchSubject = new Subject<string>()
  private destroy$ = new Subject<void>();


  sales: Sales[] = [];
  salesPropertyState: SalesPropertyState[] = [];
  filteredSales: Sales[] = []
  filterStatus: TransStatus[] = []
  totalEvent: TotalEventProperty = {} as TotalEventProperty;
  ItemSalesTeam: SalesTeam = {} as SalesTeam;
  loading = false;
  error = '';
  searchTerm: string = '';
  period_label: string = '';
  maxLength: number = 0;


  paginationConfig: PaginationConfig = {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0
  };

  sortConfig: SortConfig = {
    column: '',
    direction: ''
  };

  // Filters
  searchForm !: FormGroup;
  sortBy = 'propertyName';
  sortStatus = 'All';
  startDate: string = '';
  endDate: string = '';

  selectedStatus: string = 'All';
  isLoadingTotalEvent: boolean = false;

  constructor(
    private salesService: SalesActivitiesService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {

    // Initialize search form
    this.setupSearchSubscription();

  }

  private setupSearchSubscription(): void {
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Update URL dan load data hanya jika ada term
      if (term.trim()) {
        this.updateUrlParams();
      }
      this.loadSalesData(term);
    });
  }


  ngOnInit(): void {
    this.loadDatesFromLocalStorage();
    this.loadAllData();
    this.subscribeToRouteParams();

  }

  private loadDatesFromLocalStorage(): void {
    const startDate = localStorage.getItem('startDate');
    const endDate = localStorage.getItem('endDate');

    this.startDate = startDate || '';
    this.endDate = endDate || '';
  }

  private loadAllData(): void {
    this.loadFilterStatus();
    this.loadTotalEvent();
    this.loadSalesData(this.searchTerm);
    this.loadSalesPropertyStateData(); // Load property state data
    this.loadSalesTeam();
  }

  private subscribeToRouteParams(): void {
    // Subscribe to route query params
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.searchTerm = params['search'] || '';
        this.sortConfig.column = params['sortBy'] || '';
        this.sortConfig.direction = params['sortOrder'] as 'ASC' | 'DESC' | '' || '';
        this.paginationConfig.currentPage = Number(params['page']) || 1;

        // PERBAIKAN: Hanya gunakan satu parameter untuk filter status
        this.selectedStatus = params['filter'] || 'All';

        this.loadSalesData(this.searchTerm);
      });
  }

  public refreshDashboardData(): void {

    // Update localStorage jika diperlukan
    if (this.startDate) localStorage.setItem('startDate', this.startDate);
    if (this.endDate) localStorage.setItem('endDate', this.endDate);

    // Reload data
    this.loadAllData();
  }

  ngAfterViewInit(): void {
    // Initialize the resize handler after view is initialized
    this.handleChartResize();
  }

  loadSalesPropertyStateData(): void {
    this.loading = true;

    this.salesService.getSalesPropertyState(
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response: SalesPropertyStateResponse) => {
        this.salesPropertyState = response.data;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load sales data';
        this.loading = false;
        console.error(err);
      }
    });
  }

  loadSalesTeam(): void {
    this.loading = true;

    this.salesService.getSalesTeam(
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response) => {

        // If response.data is an array, pick the first item or handle accordingly
        this.ItemSalesTeam = Array.isArray(response.data) ? response.data[0] : response.data;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading sales Team:', error);
        this.loading = false;
        this.ItemSalesTeam = {} as SalesTeam;
      }
    });
  }

  loadTotalEvent(): void {
    this.isLoadingTotalEvent = true;

    this.salesService.getTotalEventProperty(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.totalEvent = response.data;
        this.maxLength = response.data.max_pct_value;
        this.initChartData(response.data.chart, this.maxLength);
        this.isLoadingTotalEvent = false;
      },
      error: (error) => {
        console.error('Error loading total event data:', error);
        this.isLoadingTotalEvent = false;
        this.totalEvent = {} as TotalEventProperty;
        this.initChartData([], 0); // Inisialisasi chart dengan data kosong
      }
    });
  }


  loadSalesData(searchTerm?: string): void {
    this.loading = true;

    const filterValue = this.selectedStatus === 'All' ? undefined : this.selectedStatus;

    this.salesService.getSales(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage,
      searchTerm?.trim() || '',
      this.sortConfig.column,
      filterValue,
      this.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC',
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response) => {
        this.sales = response.data;
        this.paginationConfig.totalItems = response.pagination.total_items;
        this.loading = false;
        console.log(this.sales);

      },
      error: (error) => {
        console.error('Error loading sales data:', error);
        this.loading = false;
        this.sales = [];
      }
    });
  }

  OnClickItemSale(item: any): void {
    if (!item || !item.id) return;

    // Simpan data ke localStorage
    localStorage.setItem('item_tax', JSON.stringify(item.tax));
    localStorage.setItem('item_rev_category', JSON.stringify(item.rev_category || 0));
    localStorage.setItem('item_price', JSON.stringify(item.price_estimate || 0));

    // console.log(
    //   'tax:', item.tax,
    //   'rev_category:', localStorage.getItem('item_rev_category'),
    //   'price:', item.price_estimate
    // )

    // console.log(
    //   'id:', item.id,
    //   'tax:', item.tax
    // )

    // Navigate
    this.router.navigate(['/sales-activities/sales-activities-detail', item.id]);
  }

  loadFilterStatus(): void {
    this.loading = true;

    this.salesService.getTransStatus().subscribe({
      next: (response) => {
        // PERBAIKAN: Tambahkan option 'All' di awal array
        this.filterStatus = [
          { id: 0, name: 'All' },
          ...response.data
        ];
        // console.log(this.filterStatus);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading Status data:', error);
        this.filterStatus = [{ id: 0, name: 'All' }]; // Default jika error
        this.loading = false;
      }
    });
  }

  onStatusChange(status: string): void {
    this.selectedStatus = status;
    this.paginationConfig.currentPage = 1; // Reset ke halaman 1 saat filter berubah
    this.loadSalesData(this.searchTerm);
    this.updateUrlParams();
  }

  get totalPages(): number {
    return Math.ceil(this.paginationConfig.totalItems / this.paginationConfig.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const currentPage = this.paginationConfig.currentPage;
    const totalPages = this.totalPages;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  onSearch(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const term = target.value;

    // Update searchTerm dan trigger pencarian
    this.searchTerm = term;
    this.paginationConfig.currentPage = 1;
    this.searchSubject.next(term);

    // Jika input kosong, langsung clear search dan reset data
    if (!term.trim()) {
      const queryParams: Record<string, string | number | null> = {
        page: 1,
        search: null
      };

      // Jika ada sort config, pertahankan
      if (this.sortConfig.column && this.sortConfig.direction) {
        queryParams['sortColumn'] = this.sortConfig.column;
        queryParams['sortDirection'] = this.sortConfig.direction;
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true
      });

      this.loadSalesData('');
    }
  }

  onSort(column: string): void {
    if (this.sortConfig.column === column) {
      this.sortConfig.direction = this.sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortConfig.column = column;
      this.sortConfig.direction = 'ASC';
    }

    this.paginationConfig.currentPage = 1; // Reset to first page on sort
    this.updateUrlParams();
    this.loadSalesData(this.searchTerm);
  }

  getSortIcon(column: string): string {
    // Jika bukan kolom yang sedang di-sort, tampilkan icon default
    if (this.sortConfig.column !== column) {
      return 'ri-expand-up-down-line'; // Remix icon untuk unsorted
    }

    // Jika kolom yang sedang di-sort
    if (this.sortConfig.direction === 'ASC') {
      return 'ri-arrow-up-line'; // Remix icon arrow up
    } else if (this.sortConfig.direction === 'DESC') {
      return 'ri-arrow-down-line'; // Remix icon arrow down
    }

    return 'ri-expand-up-down-line'; // Default fallback
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.paginationConfig.currentPage) {
      return;
    }

    this.paginationConfig.currentPage = page;
    this.updateUrlParams();
    this.loadSalesData(this.searchTerm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFirstPage(): void {
    this.onPageChange(1);
  }

  onLastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onPrevPage(): void {
    this.onPageChange(this.paginationConfig.currentPage - 1);
  }

  onNextPage(): void {
    this.onPageChange(this.paginationConfig.currentPage + 1);
  }

  get displayRange(): { start: number; end: number; total: number } {
    const start = (this.paginationConfig.currentPage - 1) * this.paginationConfig.itemsPerPage + 1;
    const end = Math.min(
      start + this.paginationConfig.itemsPerPage - 1,
      this.paginationConfig.totalItems
    );

    return {
      start: this.paginationConfig.totalItems === 0 ? 0 : start,
      end,
      total: this.paginationConfig.totalItems
    };
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.paginationConfig.currentPage
    };

    // Tambahkan search param jika ada nilai
    if (this.searchTerm?.trim()) {
      queryParams.search = this.searchTerm.trim();
    } else {
      queryParams.search = null; // Hapus search param
    }

    // PERBAIKAN: Tambahkan filter status ke URL params
    if (this.selectedStatus && this.selectedStatus !== 'All') {
      queryParams.filter = this.selectedStatus;
    } else {
      queryParams.filter = null; // Hapus filter param jika 'All'
    }

    // Tambahkan sort params jika ada
    if (this.sortConfig.column && this.sortConfig.direction) {
      queryParams.sortBy = this.sortConfig.column;
      queryParams.sortOrder = this.sortConfig.direction;
    } else {
      queryParams.sortBy = null;
      queryParams.sortOrder = null;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.paginationConfig.currentPage = 1;
    this.updateUrlParams();
    this.loadSalesData('');
    this.clearAllFilters();
  }

  clearAllFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = 'All';
    this.sortConfig.column = '';
    this.sortConfig.direction = '';
    this.paginationConfig.currentPage = 1;
    this.updateUrlParams();
    this.loadSalesData('');
  }

  // Method untuk trackBy (meningkatkan performa Angular)
  trackByStatus(index: number, item: any): any {
    return item.id || item.name;
  }

  // Method untuk mendapatkan class CSS berdasarkan status
  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'All': 'badge-secondary',
      'Hand Over': 'badge-success',
      'Follow Up': 'badge-warning',
      'Closing': 'badge-info',
      'Cancel': 'badge-danger'
      // Tambahkan status lain sesuai kebutuhan
    };

    return statusClasses[status] || 'badge-secondary';
  }

  // Method untuk format tampilan status (opsional)
  formatStatusDisplay(status: string): string {
    if (status === 'All') {
      return 'Semua Status';
    }
    return status;
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatPriceEstimate(price: string): string {
    if (!price || price === '0') return '-';
    const num = parseInt(price, 10);
    return num.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' });
  }


  private initChartData(chartData: any[], maxLength: number): void {
    // Ekstrak value untuk series data
    const seriesData = chartData.map(item => item.value);
    const periodLabel = chartData.map(item => item.period_label);

    // Format label periode sesuai dengan struktur baru
    // const periodLabel = chartData.map(item => {
    //   // Jika period_label berupa objek { year, month }
    //   if (typeof item.period_label === 'object' && item.period_label.year) {
    //     return `${item.period_label.year}`;
    //   }
    //   // Jika period_label berupa string "YYYY-MM"
    //   else if (typeof item.period_label === 'string') {
    //     const [year] = item.period_label.split('-');
    //     return year;
    //   }
    //   // Jika data berupa objek dengan properti year dan month langsung
    //   else if (item.year) {
    //     return `${item.year}`;
    //   }
    //   return 'Unknown';
    // });


    // console.log("Formatted Period Labels:", periodLabel);

    // Buat warna dinamis berdasarkan jumlah data
    const dataLength = chartData.length;
    const colors = Array(dataLength).fill("#B6D1C1");
    const xaxisLabelColors = Array(dataLength).fill("#B6D1C1");

    // Warna khusus untuk item terakhir
    if (dataLength > 0) {
      colors[dataLength - 1] = "#191919";
      xaxisLabelColors[dataLength - 1] = "#191919";
    }

    this.chartOptions = {
      series: [{
        name: "Persentase",
        data: seriesData,
      }],
      chart: {
        height: 400,
        width: '100%',
        type: "bar",
        events: {
          click: function (event, chartContext, config) {
            // Buat array warna default dengan panjang sesuai data
            const defaultColors = Array(dataLength).fill("#B6D1C1");

            if (config.dataPointIndex !== -1) {
              defaultColors[config.dataPointIndex] = "#126936";
            }

            chartContext.updateOptions({
              colors: defaultColors
            }, false, false);
          },
          mouseMove: function (event, chartContext, config) {
            // Buat array warna default dengan panjang sesuai data
            const defaultColors = Array(dataLength).fill("#B6D1C1");

            if (config.dataPointIndex !== -1) {
              defaultColors[config.dataPointIndex] = "#126936";
            }

            chartContext.updateOptions({
              colors: defaultColors
            }, false, false);
          },
        },
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
      },
      colors: colors,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          distributed: true,
          dataLabels: {
            position: 'top'
          }
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val + "%"
        },
        offsetY: -25,
        style: {
          fontSize: '12px',
          colors: ["#126936"],
          fontWeight: 'bold'
        },
        distributed: true,
      },
      legend: {
        show: false
      },
      grid: {
        show: true,
        position: 'back',
        borderColor: '#E6E6E6',
        xaxis: {
          lines: {
            show: false
          },
        },
        yaxis: {
          lines: {
            show: false,
          }
        },
        padding: {
          left: 20
        }
      },
      xaxis: {
        // Gunakan format label yang sudah diproses
        categories: periodLabel,
        labels: {
          rotate: -45,
          trim: false,
          hideOverlappingLabels: false,
          rotateAlways: true,
          style: {
            colors: xaxisLabelColors,
            cssClass: 'apexcharts-xaxis-label',
            fontSize: "12px",
            fontWeight: 'bold'
          },
          formatter: function (value) {
            // Format label jika diperlukan
            return value;
          },
          offsetY: 8,
          offsetX: 10
        },
        tickPlacement: 'on',
      },
      yaxis: {
        title: {
          text: ""
        },
        min: 0,
        max: maxLength,
        tickAmount: 5,
        labels: {
          formatter: function (val) {
            return val + "%"
          },
          style: {
            colors: '#666666',
            fontSize: '12px',
            fontWeight: 'normal'
          },
          offsetX: 10
        },
        axisBorder: {
          show: true,
          color: "#E6E6E6"
        },
        axisTicks: {
          show: true,
          color: "#E6E6E6",
          width: 6,
          offsetX: 0
        },
        crosshairs: {
          show: false
        },
        opposite: false
      },
    };
  }

  // Add these methods to your component
  private setupResizeListener(): void {
    window.addEventListener('resize', this.handleChartResize.bind(this));

    // Clean up listener when component is destroyed
    this.ngOnDestroy = () => {
      window.removeEventListener('resize', this.handleChartResize.bind(this));
    };
  }

  private handleChartResize(): void {
    setTimeout(() => {
      const chartContainer = document.querySelector('.chart-container') as HTMLElement;
      const chartWrapper = document.querySelector('.chart-wrapper') as HTMLElement;

      if (chartContainer && chartWrapper) {
        const windowWidth = window.innerWidth;

        // Determine if scrolling is needed based on screen size and content
        if (windowWidth < 768 && this.chartOptions.series[0].data.length > 5) {
          // For mobile with many data points, enable horizontal scrolling
          chartWrapper.classList.add('enable-scroll');
          chartContainer.style.minWidth = (this.chartOptions.series[0].data.length * 70) + 'px';
        } else if (windowWidth >= 768 && windowWidth < 992 && this.chartOptions.series[0].data.length > 7) {
          // For tablets with many data points
          chartWrapper.classList.add('enable-scroll');
          chartContainer.style.minWidth = (this.chartOptions.series[0].data.length * 60) + 'px';
        } else {
          // For desktop or fewer data points, disable scrolling
          chartWrapper.classList.remove('enable-scroll');
          chartContainer.style.minWidth = '100%';
        }
      }
    }, 100);
  }
}
